import { useQuery } from "@tanstack/react-query";
import { useAppRouter } from "~/hooks/use-app-router";

import { apiGqlClient } from "~/libs/gql";
import { graphql } from "~/types/__generated/gql";
import {
	GetPaymentVerificationsQuery,
	PAYMENT_VERIFICATION_STATUS,
} from "~/types/__generated/gql/graphql";

const query = /* GraphQL */ `
	query GetPaymentVerifications(
		$statuses: [PAYMENT_VERIFICATION_STATUS!]
		$from: String
		$to: String
	) {
		getPaymentVerifications(statuses: $statuses, from: $from, to: $to) {
			_id
			createdAt
			updatedAt
			voidedAt
			user
			files
			attachment {
				name
				size
				url
				extension
			}
			status
			receivedAmount
			receivedDate
			paymentStage
			currency
			reference {
				counter
				number

				_counter {
					_id
					type
					prefix
					suffix_digit
					isPredefinedFormat
					isCustomFormat
				}
			}

			counterConfig {
				file {
					counter
					index
					increment

					_counter {
						_id
						type
						prefix
						suffix_digit
						isPredefinedFormat
						isCustomFormat
					}
				}
			}
			_user {
				firstName
				lastName
				avatar
			}
			_files {
				_id
				createdAt
				agent
				client
				process
				_agent {
					name
				}
				_client {
					name
				}
				_process {
					name
				}
				products {
					_id
					createdAt
					updatedAt
					originalPrice
					originalPriceAddons {
						vat {
							percentage
							amount
						}
					}
					listedPrice
					unitPrice
					quantity
					subtotal
					subtotalAddons {
						additionalFee {
							isDivided
							type
							value
							amount
							additionalFee
						}
						fixDiscount {
							isDivided
							type
							value
							amount
							fixDiscount
						}
						discount {
							isDivided
							type
							value
							amount
						}
						vat {
							isIncluded
							percentage
							amount
						}
						adjustment {
							amount
						}
					}
					multiCurrencyConfig {
						selectedCurrency
						priceConfigs {
							code
							price
							rate
							isPrimaryCurrency
						}
					}
					noVat
					netAmount
					grossAmount
					cancelledAmount
					category
					type
					product
					options {
						_id
						createdAt
						updatedAt
						originalPrice
						originalPriceAddons {
							vat {
								percentage
								amount
							}
						}
						listedPrice
						quantity
						subtotal
						subtotalAddons {
							additionalFee {
								isDivided
								type
								value
								amount
								additionalFee
							}
							fixDiscount {
								isDivided
								type
								value
								amount
								fixDiscount
							}
							discount {
								isDivided
								type
								value
								amount
							}
							vat {
								isIncluded
								percentage
								amount
							}
							adjustment {
								amount
							}
						}
						multiCurrencyConfig {
							selectedCurrency
							priceConfigs {
								code
								price
								rate
								isPrimaryCurrency
							}
						}
						netAmount
						grossAmount
						cancelledAmount
						option

						_option {
							_id
							name
						}
					}

					_product {
						_id
						name
					}
				}
				cancelledProducts {
					_id
					createdAt
					updatedAt
					originalPrice
					originalPriceAddons {
						vat {
							percentage
							amount
						}
					}
					listedPrice
					unitPrice
					quantity
					subtotal
					subtotalAddons {
						additionalFee {
							isDivided
							type
							value
							amount
							additionalFee
						}
						fixDiscount {
							isDivided
							type
							value
							amount
							fixDiscount
						}
						discount {
							isDivided
							type
							value
							amount
						}
						vat {
							isIncluded
							percentage
							amount
						}
						adjustment {
							amount
						}
					}
					multiCurrencyConfig {
						selectedCurrency
						priceConfigs {
							code
							price
							rate
							isPrimaryCurrency
						}
					}
					noVat
					netAmount
					grossAmount
					cancelledAmount
					category
					type
					product
					options {
						_id
						createdAt
						updatedAt
						originalPrice
						originalPriceAddons {
							vat {
								percentage
								amount
							}
						}
						listedPrice
						quantity
						subtotal
						subtotalAddons {
							additionalFee {
								isDivided
								type
								value
								amount
								additionalFee
							}
							fixDiscount {
								isDivided
								type
								value
								amount
								fixDiscount
							}
							discount {
								isDivided
								type
								value
								amount
							}
							vat {
								isIncluded
								percentage
								amount
							}
							adjustment {
								amount
							}
						}
						multiCurrencyConfig {
							selectedCurrency
							priceConfigs {
								code
								price
								rate
								isPrimaryCurrency
							}
						}
						netAmount
						grossAmount
						cancelledAmount
						option

						_option {
							_id
							name
						}
					}

					_product {
						_id
						name
					}
				}
				counterConfigs {
					file {
						counter
						index
						reference

						_counter {
							_id
							type
							prefix
							suffix_digit
							isPredefinedFormat
							isCustomFormat
						}
					}
					invoice {
						counter
						index
						reference

						_counter {
							_id
							type
							prefix
							suffix_digit
							isPredefinedFormat
							isCustomFormat
						}
					}
					monthlyStatement {
						counter
						index
						reference

						_counter {
							_id
							type
							prefix
							suffix_digit
							isPredefinedFormat
							isCustomFormat
						}
					}
					task {
						counter
						index
						reference

						_counter {
							_id
							type
							prefix
							suffix_digit
							isPredefinedFormat
							isCustomFormat
						}
					}
				}
				taskStatus
				multiCurrencyConfig {
					selectedCurrency
					currencyConfigs {
						code
						rate
						isPrimaryCurrency
					}
				}
				_receipt {
					_id
					createdAt
					updatedAt
					company
					user
					isFullyPaid
					payments {
						paidAt
						voidedAt
						transactionId
						paymentVerificationId
						paymentStage
						payments {
							amount
							voidedAmount
							payment_type
							bank_received_date
							multiCurrencyConfig {
								selectedCurrency
								currencyConfigs {
									code
									rate
									isPrimaryCurrency
								}
							}
						}
					}
				}
				fileSuffixComment
				receiptSuffixComment
			}
			_transaction {
				_id
				reference {
					number
				}
				_agents {
					name
				}
				_clients {
					name
				}
			}
			paymentPurpose
			receivedStatus
			remarks
		}
	}
`;

export type GetPaymentVerificationsResults =
	GetPaymentVerificationsQuery["getPaymentVerifications"];

export const getPaymentVerificationsQueryKey = () => [
	"getPaymentVerifications",
];

export const useGetPaymentVerifications = (
	statuses: PAYMENT_VERIFICATION_STATUS[] | null | undefined,
	enabled?: boolean,
) => {
	const { appQuery } = useAppRouter();
	return useQuery({
		enabled: enabled ?? true,
		queryKey: getPaymentVerificationsQueryKey(),
		queryFn: async () =>
			apiGqlClient
				.request(graphql(query), {
					from: appQuery.from,
					to: appQuery.to,
					statuses: statuses,
				})
				.then((res) => res.getPaymentVerifications),
	});
};
